import styled from '@emotion/styled';
import { usePathname } from 'next/navigation';
import { Color } from '../../../styles/Color';
import { Css } from '../../../ui';
import { ISecurityCheckpoint } from '../domain/models/ISecurityCheckpoint';
import Person from '../../../assets/security/person.svg';
import { getSecurityWaitTimeIconColor } from '../domain/utils/getSecurityWaitTimeIconColor';
import { getSecurityWaitTimeIconData } from '../domain/utils/getSecurityWaitTimeIconData';
import { terminalMap } from '../domain/utils/terminalMap';
import { convertTimeTo12HourFormat } from '../../../utils/convertTimeTo12HourFormat';
import { getAirportMapUrlForPoiId } from '../../map/domain/helpers/getAirportMapUrl';
import { getCurrentAirportCodeFromSlug } from '../../../components/common/navigation/utils/getCurrentAirportCode';
import {
	InfoCard,
	InfoCardDescription,
	InfoCardDivider,
	InfoCardIcons,
	InfoCardLink,
	InfoCardRemark,
	InfoCardTitle,
	InfoCardTitleSection,
	InfoCardTop,
	InfoCardTopRightSection,
} from '../../../components/common/cards/InfoCard/InfoCard.styles';

interface ISecurityCard {
	checkpoint: ISecurityCheckpoint;
	poiId?: string;
}

export default function SecurityCard({ checkpoint, poiId }: ISecurityCard) {
	const { id, openTime, closeTime, waitSeconds, isOpen, remark, override } =
		checkpoint;
	const pathname = usePathname();
	const hasOverrideMessage = override && !!remark;

	const airportCode = getCurrentAirportCodeFromSlug(pathname);
	const iconData = getSecurityWaitTimeIconData(
		waitSeconds,
		isOpen,
		hasOverrideMessage,
	);

	const airportMapUrlForPoi = getAirportMapUrlForPoiId(airportCode, poiId);
	const formattedOpenTime = convertTimeTo12HourFormat(openTime);
	const formattedCloseTime = convertTimeTo12HourFormat(closeTime);

	const { terminal, color } = terminalMap[id] || {};

	const getWaitTimeStatus = () => {
		if (!isOpen) return 'Closed';
		if (!waitSeconds) return 'Open';
		if (waitSeconds) return `${Math.ceil(waitSeconds / 60)} min`;

		return null;
	};

	return (
		<InfoCard>
			<InfoCardTop>
				<div>
					<InfoCardTitleSection>
						{terminal && color && (
							<InfoCardTitleIcon color={color} className="notranslate">
								{terminal}
							</InfoCardTitleIcon>
						)}
						<InfoCardTitle>{checkpoint.name}</InfoCardTitle>
					</InfoCardTitleSection>
					<InfoCardDescription>
						Hours: {formattedOpenTime} - {formattedCloseTime}
					</InfoCardDescription>
				</div>
				<InfoCardTopRightSection>
					<InfoCardIcons aria-label={iconData.ariaLabel}>
						{[...Array(4)].map((_, index) => (
							<PersonIcon
								key={index} /* eslint-disable-line react/no-array-index-key */
								fill={getSecurityWaitTimeIconColor(index, 4, iconData.number)}
							/>
						))}
					</InfoCardIcons>
					{!hasOverrideMessage && (
						<InfoCardRemark>{getWaitTimeStatus()}</InfoCardRemark>
					)}
				</InfoCardTopRightSection>
			</InfoCardTop>

			{hasOverrideMessage && <OverrideMessage>{remark}</OverrideMessage>}

			<InfoCardDivider />

			<InfoCardLink
				href={airportMapUrlForPoi}
				target="_blank"
				aria-label={`Get directions to ${checkpoint.name}`}
			>
				Get directions
			</InfoCardLink>
		</InfoCard>
	);
}

const InfoCardTitleIcon = styled.div<{ color: string }>`
	width: 24px;
	height: 24px;
	background-color: ${({ color }) => color};
	color: ${Color.Neutral1};
	display: flex;
	align-items: center;
	justify-content: center;
	font-weight: 500;
	border-radius: 4px;
`;

const PersonIcon = styled(Person)<{ fill: string }>`
	path {
		fill: ${({ fill }) => fill};
	}
`;

const OverrideMessage = styled.div`
	margin: ${Css.pxToRem(16)} 0;
	padding: ${Css.pxToRem(8)} ${Css.pxToRem(8)};
	border: 1px solid ${Color.Neutral3};
	border-radius: 8px;
	color: ${Color.OnBackground};
	background-color: ${Color.Neutral1};
`;
